import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import FormControlLabel from '@mui/material/FormControlLabel';
import isEmpty from '../../../../is-empty';
import AutoCompleteField from '../../AutoCompleteField/AutoCompleteField';
import ModalDialog from '../../Dialog/ModalDialog';
import { EvoraVerticalTabs, VerticalTabCanvas } from '../../Tabs/Tabs';
import Aux from '../../../../hoc/Auxilary/Auxilary';
import SectionTitle from '../../Typography/SubSectionHeading';
import Switch from '../../Switch/Switch';

const PERMISSION_TYPES = ['read', 'create', 'delete', 'update', 'complete', 'review', 'signoff', 'print', 'unlock'];

const EMPTY_FORM = { role: '', roleSel: null, process: '', procSel: null, departments: [], depSels: [] };

const processSettings = (settingsData) => {
    const perms = {};
    PERMISSION_TYPES.forEach(type => {
        perms[type] = (settingsData[type] || []).map(setting => {
            const roleSel = setting.role
                ? settingsData.roles?.find(x => x.value === setting.role) ?? null
                : null;
            const procSel = setting.process
                ? settingsData.processes?.find(x => x.value === setting.process) ?? null
                : null;
            const depSels = (setting.departments || [])
                .map(dep => settingsData.departments?.find(x => x.value === dep))
                .filter(Boolean);
            const depString = depSels.map(d => d.label).join(', ');
            return { ...setting, roleSel, procSel, depSels, depString };
        });
    });
    return perms;
};

const Screen = ({ settingsData, callBack, toggleDialog }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [tabKey, setTabKey] = useState(0);
    const [screenId, setScreenId] = useState('');
    const [all, setAll] = useState(false);
    const [internal, setInternal] = useState(false);
    const [permissions, setPermissions] = useState({});
    const [roles, setRoles] = useState([]);
    const [processes, setProcesses] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [saving, setSaving] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogType, setDialogType] = useState('');
    const [dialogIndex, setDialogIndex] = useState(null);
    const [dialogIsNew, setDialogIsNew] = useState(false);
    const [dialogForm, setDialogForm] = useState(EMPTY_FORM);
    const [dialogErrors, setDialogErrors] = useState({});

    const tabLabels = PERMISSION_TYPES.map(type => ({ name: t(type), disabled: false }));

    useEffect(() => {
        if (isEmpty(settingsData)) return;
        setScreenId(settingsData._id || '');
        setAll(settingsData.all || false);
        setInternal(settingsData.internal || false);
        setRoles(settingsData.roles || []);
        setProcesses(settingsData.processes || []);
        setDepartments(settingsData.departments || []);
        setPermissions(processSettings(settingsData));
    }, [settingsData]);

    const activeType = PERMISSION_TYPES[tabKey];
    const activePermissions = permissions[activeType] || [];

    const openDialog = (index) => {
        const setting = activePermissions[index];
        setDialogForm({
            role: setting.role || '',
            roleSel: setting.roleSel || null,
            process: setting.process || '',
            procSel: setting.procSel || null,
            departments: setting.departments || [],
            depSels: setting.depSels || []
        });
        setDialogErrors({});
        setDialogType(activeType);
        setDialogIndex(index);
        setDialogIsNew(false);
        setDialogOpen(true);
    };

    const addRole = () => {
        setDialogForm({ ...EMPTY_FORM });
        setDialogErrors({});
        setDialogType(activeType);
        setDialogIndex(null);
        setDialogIsNew(true);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setDialogErrors({});
    };

    const changeDialogAuto = (name, data) => (e, newValue) => {
        setDialogForm(prev => ({
            ...prev,
            [name]: newValue,
            [data]: newValue?.value || ''
        }));
        if (data === 'role') setDialogErrors(prev => ({ ...prev, role: undefined }));
    };

    const changeDialogDepartments = (e, newValue) => {
        const depSels = (newValue || []).map(row => ({ value: row.value, label: row.label }));
        setDialogForm(prev => ({
            ...prev,
            departments: depSels.map(d => d.value),
            depSels
        }));
    };

    const alert = (message, severity) => {
        const title = severity === 'success' ? t('success') : t('error');
        dispatch({ type: 'CREATE_ALERT', payload: { message, title, severity } });
    };

    const saveScreen = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const result = await axios.post('/api/screen/save', {
                screen: settingsData.screen, all, internal, _id: screenId
            });
            if (result.data.id) setScreenId(result.data.id);
            callBack();
            alert(result.data.message, 'success');
        } catch (err) {
            alert(err.response.data, 'error');
        }
        setSaving(false);
    };

    const ensureScreenId = async () => {
        if (screenId) return screenId;
        const result = await axios.post('/api/screen/save', {
            screen: settingsData.screen, all, internal, _id: ''
        });
        const newId = result.data.id;
        setScreenId(newId);
        return newId;
    };

    const saveRole = async () => {
        if (!dialogForm.role) {
            setDialogErrors({ role: t('required') });
            return;
        }
        if (saving) return;
        setSaving(true);
        try {
            const sid = await ensureScreenId();
            const result = await axios.post('/api/screen/role', {
                type: dialogType,
                role: dialogForm.role,
                departments: dialogForm.departments,
                screenId: sid,
                process: dialogForm.process
            });
            alert(result.data.message, 'success');
            closeDialog();
            callBack();
        } catch (err) {
            alert(err.response.data, 'error');
        }
        setSaving(false);
    };

    const deleteRole = async () => {
        if (dialogIsNew) { closeDialog(); return; }
        if (saving) return;
        setSaving(true);
        try {
            const setting = permissions[dialogType]?.[dialogIndex];
            const result = await axios.post('/api/screen/roledelete', {
                type: dialogType, role: setting.role, screenId
            });
            alert(result.data.message, 'success');
            closeDialog();
            callBack();
        } catch (err) {
            alert(err.response.data, 'error');
        }
        setSaving(false);
    };

    const dialogButtons = (
        <>
            <Button onClick={saveRole} variant="contained" color="primary" disabled={saving}>
                {t('save')}
            </Button>
            <Button onClick={closeDialog} variant="outlined" color="secondary">
                {t('cancel')}
            </Button>
            {!dialogIsNew && (
                <Button onClick={deleteRole} variant="outlined" sx={{ color: 'error.main', borderColor: 'error.main' }} disabled={saving}>
                    {t('delete')}
                </Button>
            )}
        </>
    );

    const renderPermissionTab = (row) => {
        const type = row.name.toLowerCase();
        const settings = permissions[type] || [];
        return (
            <Grid container spacing={3}>
                <Grid container size={12}>
                    <SectionTitle title={row.name} add={addRole} addTitle={t('add')} />
                    <hr />
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '30%' }}>{t('role')}</TableCell>
                                    <TableCell sx={{ width: '30%' }}>{t('process')}</TableCell>
                                    <TableCell>{t('departments')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {settings.length > 0 ? settings.map((setting, i) => (
                                    <TableRow
                                        key={`${type}-row-${i}`}
                                        onClick={() => openDialog(i)}
                                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                                    >
                                        <TableCell>{setting.roleSel?.label || ''}</TableCell>
                                        <TableCell>{setting.procSel?.label || ''}</TableCell>
                                        <TableCell>{setting.depString || ''}</TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            {t('noPermissionsFound')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        );
    };

    const securityTabs = () =>
        tabLabels.map((row, i) => (
            <VerticalTabCanvas value={tabKey} index={i} key={`security-tab-${i}`}>
                <Grid container spacing={3} size={12}>
                    {renderPermissionTab(row)}
                </Grid>
            </VerticalTabCanvas>
        ));

    return (
        <Aux>
            <Grid container spacing={2} sx={{ justifyContent: 'flex-end' }}>
                <Grid>
                    <FormControlLabel
                        control={<Switch checked={all} onChange={() => setAll(v => !v)} name="all" />}
                        label={t('all')}
                        labelPlacement="start"
                    />
                </Grid>
                <Grid>
                    <FormControlLabel
                        control={<Switch checked={internal} onChange={() => setInternal(v => !v)} name="internal" />}
                        label={t('internal')}
                        labelPlacement="start"
                    />
                </Grid>
            </Grid>

            <EvoraVerticalTabs TabContents={securityTabs} tabLabels={tabLabels} tabKey={tabKey} changeTab={(e, v) => setTabKey(v)} />

            <Grid container sx={{ justifyContent: 'flex-end' }} size={12}>
                <Grid>
                    <Button onClick={toggleDialog} variant="text" color="secondary">{t('close')}</Button>
                    <Button onClick={saveScreen} variant="text" color="primary" disabled={saving}>{t('save')}</Button>
                </Grid>
            </Grid>

            <ModalDialog
                title={dialogIsNew ? t('addPermission') : t('editPermission')}
                toggleDialog={closeDialog}
                dialogStatus={dialogOpen}
                fullWidth
                maxWidth="sm"
                buttonStack={dialogButtons}
            >
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <AutoCompleteField
                            value={dialogForm.roleSel}
                            options={roles}
                            onChange={changeDialogAuto('roleSel', 'role')}
                            error={dialogErrors.role != null}
                            helperText={dialogErrors.role}
                            label={t('role')}
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <AutoCompleteField
                            value={dialogForm.procSel}
                            options={processes}
                            onChange={changeDialogAuto('procSel', 'process')}
                            label={t('process')}
                        />
                    </Grid>
                    <Grid size={12}>
                        <AutoCompleteField
                            multiple
                            stayOpen
                            value={dialogForm.depSels}
                            options={departments}
                            getOptionLabel={(option) => option.label || ''}
                            onChange={changeDialogDepartments}
                            label={t('departments')}
                        />
                    </Grid>
                </Grid>
            </ModalDialog>
        </Aux>
    );
};

export default Screen;
